/**
 * Observation model for Espace Évolution sessions.
 *
 * Reworked after the quality meeting of 2026-09-29 (PV ae45 §4):
 *   - "Lien avec les choix précédents" is reformulated: not a memory test but
 *     the capacity to relate today's themes to earlier ones and to one's
 *     situation as a whole. The intervenant·e brings the reminder.
 *   - "Défi" is added, at card-choice level: the participant is invited to
 *     formulate a small challenge for the week, a direction, or the atelier(s)
 *     where they see themselves working a chosen theme.
 *   - "Facilité de prise des cartes" became a yes / no in October 2026, then
 *     went back to its five levels on 2026-10-30 at Yves's request. Choosing a
 *     letter also writes the yes / no (`takes_cards`, A = no card) so the
 *     Observatoire's card-taking figures stay whole. Entries recorded with the
 *     yes / no only stay readable (TAKES_CARDS below).
 *
 * Five dimensions are rated A → E (A weakest signal, E strongest). Every
 * level carries a `short` form (reports, dense UI) and a `desc` (the full
 * criteria shown while rating). The wording IS the assessment model: change
 * it deliberately, with the Éveil team, not as a refactor.
 */

export type ScoreLetter = 'A' | 'B' | 'C' | 'D' | 'E';
export const SCORE_LETTERS: ScoreLetter[] = ['A', 'B', 'C', 'D', 'E'];

export type DimLang = 'fr' | 'en';
type L10n = Record<DimLang, string>;

export interface ScoreLevelDef {
  short: L10n;
  desc: L10n;
}

export type ScoreKey = 'prise_cartes' | 'lien_precedents' | 'expression' | 'responsabilisation' | 'defi';
export type ScoreField = 'score_prise_cartes' | 'score_lien_precedents' | 'score_expression' | 'score_responsabilisation' | 'score_defi';

export interface ScoreDimension {
  /** Stable key, also used in history payloads. */
  key: ScoreKey;
  /** Column / API field name on session_participants. */
  field: ScoreField;
  label: L10n;
  /** One-line prompt under the label. */
  help: L10n;
  /** Compact label for the copied report's OBSERVATIONS block. */
  reportLabel: L10n;
  levels: Record<ScoreLetter, ScoreLevelDef>;
}

export const SCORE_DIMENSIONS: ScoreDimension[] = [
  {
    key: 'prise_cartes',
    field: 'score_prise_cartes',
    label: {
      fr: 'Facilité de prise des cartes',
      en: 'Ease of taking cards',
    },
    help: {
      fr: 'Comment le·la participant·e s’approprie-t-il/elle les cartes présentées ?',
      en: 'How does the participant appropriate the cards presented?',
    },
    reportLabel: { fr: 'Prise des cartes', en: 'Taking cards' },
    levels: {
      A: {
        short: { fr: 'Aucune carte prise', en: 'No card taken' },
        desc: {
          fr: 'Ne prend aucune carte : n’interagit d’aucune manière avec les cartes, ou refuse explicitement de participer.',
          en: 'Takes no card at all: does not interact with the cards in any way, or outright refuses to participate.',
        },
      },
      B: {
        short: { fr: 'Approche hésitante', en: 'Hesitant approach' },
        desc: {
          fr: 'Pointe les cartes du doigt, en prend une avec hésitation, la retourne face cachée ou la range au bord de la table. Un malaise face aux cartes est observable.',
          en: 'Points at cards, takes one hesitantly, turns it face down or leaves it at the edge of the table. A visible unease with the cards.',
        },
      },
      C: {
        short: { fr: 'Prend les cartes vers soi', en: 'Takes cards to themselves' },
        desc: {
          fr: 'Parvient à choisir des cartes et à les prendre vers soi, mais parfois encore avec retenue : par exemple, les laisse orientées telles qu’elles ont été présentées au lieu de les tourner pour les lire facilement.',
          en: 'Manages to choose cards and bring them to themselves, though sometimes still with reserve: e.g. leaves them oriented as presented instead of turning them to read them easily.',
        },
      },
      D: {
        short: { fr: 'À l’aise, choix mesuré', en: 'At ease, measured choice' },
        desc: {
          fr: 'Prend les cartes avec aisance — ni trop, ni trop peu. Visiblement à l’aise pour s’approprier les thématiques inscrites : on observe qu’il/elle les prend à soi.',
          en: 'Takes cards with ease — not too many, not too few. Clearly comfortable appropriating the written topics: you can see them making the cards their own.',
        },
      },
      E: {
        short: { fr: 'Appropriation et mise en lien', en: 'Appropriation and linking' },
        desc: {
          fr: 'Grande aisance ; va jusqu’à organiser les cartes de manière signifiante et associative — une thématique centrale entourée de cartes adjacentes, ou des cartes du passé, du présent (en travail) et de la suite pressentie. Témoigne d’une pensée en liens et d’une forte appropriation des thématiques.',
          en: 'Great ease; even arranges the cards meaningfully and associatively — a central theme surrounded by adjacent cards, or cards from their past, their present work and what they sense comes next. Shows thinking in connections and strong appropriation of the topics.',
        },
      },
    },
  },
  {
    key: 'lien_precedents',
    field: 'score_lien_precedents',
    label: {
      fr: 'Mise en lien des thématiques',
      en: 'Linking the themes',
    },
    help: {
      fr: 'Relie-t-il/elle les thématiques du jour aux thématiques antérieures et à sa situation dans son ensemble ? (C’est à l’intervenant·e d’amener le rappel de la séance précédente.)',
      en: 'Do they relate today’s themes to earlier ones and to their situation as a whole? (The intervenant brings the reminder of the previous session.)',
    },
    reportLabel: { fr: 'Mise en lien des thématiques', en: 'Linking the themes' },
    levels: {
      A: {
        short: { fr: 'Aucune mise en lien', en: 'No linking' },
        desc: {
          fr: 'Même avec le rappel de la séance précédente, ne relie pas les thématiques du jour à celles d’avant ni à sa situation : chaque séance reste isolée.',
          en: 'Even when reminded of the previous session, does not relate today’s themes to earlier ones or to their situation: each session stays isolated.',
        },
      },
      B: {
        short: { fr: 'Lien ponctuel, sur rappel', en: 'Occasional link, when prompted' },
        desc: {
          fr: 'Reconnaît un lien quand l’intervenant·e le nomme, mais ne le développe pas ; les thématiques restent juxtaposées.',
          en: 'Acknowledges a link when the intervenant names it, but does not develop it; the themes remain side by side.',
        },
      },
      C: {
        short: { fr: 'Relie de lui/elle-même', en: 'Links on their own' },
        desc: {
          fr: 'Fait spontanément le lien entre les thématiques actuelles et antérieures, ou explique pourquoi elles ont changé.',
          en: 'Spontaneously relates current themes to earlier ones, or explains why they changed.',
        },
      },
      D: {
        short: { fr: 'Articule avec sa situation', en: 'Articulates with their situation' },
        desc: {
          fr: 'Relie les thématiques entre elles et à sa situation concrète (mesure, ateliers, quotidien) ; voit comment elles se nourrissent.',
          en: 'Relates the themes to one another and to their concrete situation (measure, ateliers, daily life); sees how they feed each other.',
        },
      },
      E: {
        short: { fr: 'Vision d’ensemble', en: 'Overall view' },
        desc: {
          fr: 'Situe ses thématiques dans son parcours (avant / maintenant / la suite) et en tire une lecture globale de sa situation, formulée avec ses mots.',
          en: 'Places their themes in their path (before / now / next) and draws an overall reading of their situation, in their own words.',
        },
      },
    },
  },
  {
    key: 'expression',
    field: 'score_expression',
    label: {
      fr: 'Facilité à s’exprimer autour des thématiques choisies',
      en: 'Ease of expression around the chosen topics',
    },
    help: {
      fr: 'Avec quelle aisance parle-t-il/elle des thématiques choisies ?',
      en: 'How easily do they talk about the chosen topics?',
    },
    reportLabel: { fr: 'Expression autour des thématiques', en: 'Expression around topics' },
    levels: {
      A: {
        short: { fr: 'Ne s’exprime pas', en: 'Does not express' },
        desc: {
          fr: 'Ne s’exprime pas autour des cartes : reste silencieux·se, répond à côté, ou refuse d’en parler même avec sollicitation.',
          en: 'Does not speak about the cards: stays silent, answers off-topic, or refuses to talk about them even when prompted.',
        },
      },
      B: {
        short: { fr: 'Expression minimale', en: 'Minimal expression' },
        desc: {
          fr: 'S’exprime par mots isolés ou phrases très courtes (« c’est dur », « ça va ») ; a besoin d’être relancé·e à chaque étape ; le lien entre le propos et la carte reste vague ou plaqué.',
          en: 'Speaks in isolated words or very short sentences (“it’s hard”, “it’s fine”); needs prompting at every step; the link between what is said and the card stays vague or superficial.',
        },
      },
      C: {
        short: { fr: 'S’exprime avec soutien', en: 'Expresses with support' },
        desc: {
          fr: 'Parvient à dire avec ses mots ce que la thématique représente pour lui/elle et à donner un exemple concret de sa situation ; le discours reste factuel et peut nécessiter quelques relances.',
          en: 'Manages to say in their own words what the topic means to them and give a concrete example from their situation; the account stays factual and may need a few prompts.',
        },
      },
      D: {
        short: { fr: 'Discours fluide et personnel', en: 'Fluent, personal account' },
        desc: {
          fr: 'S’exprime spontanément et de façon structurée autour des thématiques : relie la carte à son vécu, nomme ce que cela lui fait, distingue ce qui a changé ou non. Peu ou pas de relances nécessaires.',
          en: 'Speaks spontaneously and in a structured way: connects the card to their lived experience, names what it does to them, distinguishes what has changed and what has not. Little to no prompting needed.',
        },
      },
      E: {
        short: { fr: 'Élaboration riche, dialogue', en: 'Rich elaboration, dialogue' },
        desc: {
          fr: 'Élabore librement autour des thématiques : nuance, met en perspective (avant / maintenant / la suite), s’appuie sur les échos du groupe, voire rebondit sur ce que disent les autres et fait avancer le partage collectif.',
          en: 'Elaborates freely: nuances, puts things in perspective (before / now / next), draws on the group’s echoes, even builds on what others say and moves the collective sharing forward.',
        },
      },
    },
  },
  {
    key: 'responsabilisation',
    field: 'score_responsabilisation',
    label: {
      fr: 'Responsabilisation dans le chemin à parcourir',
      en: 'Ownership of the road ahead',
    },
    help: {
      fr: 'Dans quelle mesure se positionne-t-il/elle comme acteur·rice de son parcours ?',
      en: 'To what extent do they position themselves as the actor of their own path?',
    },
    reportLabel: { fr: 'Responsabilisation', en: 'Ownership' },
    levels: {
      A: {
        short: { fr: 'Ne se responsabilise pas', en: 'No ownership' },
        desc: {
          fr: 'Situe le changement entièrement à l’extérieur de lui/elle — p. ex. « pour avoir confiance en moi, j’ai besoin de l’extérieur, d’être valorisé·e ». Attend que la solution vienne des autres ou des circonstances.',
          en: 'Places change entirely outside themselves — e.g. “to have self-confidence, I need the outside, to be valued”. Waits for the solution to come from others or from circumstances.',
        },
      },
      B: {
        short: { fr: 'Conscience naissante', en: 'Emerging awareness' },
        desc: {
          fr: 'Commence à reconnaître sa part dans sa situation (« je sais que ça dépend aussi de moi ») mais reste dans le constat : ne formule pas de pas concret et revient vite aux causes externes.',
          en: 'Begins to recognise their part in the situation (“I know it also depends on me”) but stays at the level of observation: formulates no concrete step and quickly falls back on external causes.',
        },
      },
      C: {
        short: { fr: 'Reconnaît sa part', en: 'Recognises their part' },
        desc: {
          fr: 'Identifie ce qui dépend de lui/elle dans la thématique choisie et évoque une piste d’action personnelle, même modeste ou encore hypothétique (« je pourrais essayer de… »).',
          en: 'Identifies what depends on them in the chosen topic and mentions a personal course of action, however modest or still hypothetical (“I could try to…”).',
        },
      },
      D: {
        short: { fr: 'Acteur·rice de son chemin', en: 'Actor of their path' },
        desc: {
          fr: 'Se positionne en acteur·rice : nomme des pas concrets entrepris ou à entreprendre, assume les difficultés rencontrées sans les attribuer aux autres, demande de l’aide de façon ciblée.',
          en: 'Positions themselves as the actor: names concrete steps taken or planned, owns the difficulties met without attributing them to others, asks for help in a targeted way.',
        },
      },
      E: {
        short: { fr: 'Autonomie engagée', en: 'Engaged autonomy' },
        desc: {
          fr: 'Porte son chemin de manière autonome : rend compte d’actions menées entre les séances, en tire des apprentissages, ajuste lui/elle-même la suite (« j’ai essayé X, ça a donné Y, maintenant je veux Z ») et mobilise les ressources autour de lui/elle comme des appuis — non comme des conditions.',
          en: 'Carries their path autonomously: reports on actions taken between sessions, draws learnings, adjusts the next step themselves (“I tried X, it gave Y, now I want Z”) and mobilises the resources around them as supports — not as preconditions.',
        },
      },
    },
  },
  {
    key: 'defi',
    field: 'score_defi',
    label: {
      fr: 'Défi — projection dans l’action',
      en: 'Challenge — projection into action',
    },
    help: {
      fr: 'Invité·e à formuler un petit défi pour la semaine, une direction dans son quotidien et son parcours, ou l’atelier où travailler une thématique choisie : qu’en fait-il/elle ?',
      en: 'Invited to formulate a small challenge for the week, a direction in daily life and in their path, or the atelier where to work a chosen theme: what do they do with it?',
    },
    reportLabel: { fr: 'Défi', en: 'Challenge' },
    levels: {
      A: {
        short: { fr: 'Aucun défi', en: 'No challenge' },
        desc: {
          fr: 'Ne formule aucun défi ni direction, même avec l’invitation ; ne voit pas où travailler ses thématiques.',
          en: 'Formulates no challenge or direction, even when invited; does not see where to work on their themes.',
        },
      },
      B: {
        short: { fr: 'Défi vague ou soufflé', en: 'Vague or prompted challenge' },
        desc: {
          fr: 'Un défi n’apparaît que s’il est proposé par l’intervenant·e, ou reste trop vague pour être tenu (« faire des efforts »).',
          en: 'A challenge only appears when the intervenant proposes it, or stays too vague to be kept (“make an effort”).',
        },
      },
      C: {
        short: { fr: 'Petit défi concret, avec aide', en: 'Small concrete challenge, with help' },
        desc: {
          fr: 'Formule avec de l’aide un défi concret et borné dans le temps, ou nomme un atelier où travailler la thématique.',
          en: 'With help, formulates a concrete, time-bound challenge, or names an atelier where to work the theme.',
        },
      },
      D: {
        short: { fr: 'Défi autonome et situé', en: 'Autonomous, situated challenge' },
        desc: {
          fr: 'Formule seul·e un défi concret et réaliste, et le relie à une direction ou à un atelier précis de son programme.',
          en: 'Formulates on their own a concrete, realistic challenge and ties it to a direction or to a specific atelier of their programme.',
        },
      },
      E: {
        short: { fr: 'Boucle de défi', en: 'Challenge loop' },
        desc: {
          fr: 'Revient sur le défi précédent, dit ce qu’il/elle en a tiré, et en pose un nouveau en conséquence.',
          en: 'Comes back to the previous challenge, says what they drew from it, and sets a new one accordingly.',
        },
      },
    },
  },
];

/** The yes / no card-taking indicator used in October 2026. No longer asked;
 *  kept to show entries recorded with it, and written from the A–E letter. */
export const TAKES_CARDS = {
  field: 'takes_cards' as const,
  label: { fr: 'Prise des cartes (oui / non)', en: 'Taking cards (yes / no)' },
  help: {
    fr: 'Prend-il/elle des cartes ? (On distingue uniquement les personnes qui prennent les cartes de celles qui ne les prennent pas.)',
    en: 'Do they take cards? (Only whether the person takes cards or not.)',
  },
  reportLabel: { fr: 'Prise des cartes', en: 'Taking cards' },
  yes: { short: { fr: 'Prend les cartes', en: 'Takes cards' } },
  no: { short: { fr: 'Ne prend pas de carte', en: 'Takes no card' } },
};

export const SCORE_FIELDS = SCORE_DIMENSIONS.map((d) => d.field);

export function isScoreLetter(v: unknown): v is ScoreLetter {
  return typeof v === 'string' && (SCORE_LETTERS as string[]).includes(v);
}

/** Numeric position of a letter (A=1 … E=5) — for charts. */
export function letterIndex(letter: ScoreLetter): number {
  return SCORE_LETTERS.indexOf(letter) + 1;
}

export type Horizon = 'court' | 'moyen' | 'long';
export const HORIZONS: Horizon[] = ['court', 'moyen', 'long'];
export const HORIZON_LABELS: Record<Horizon, L10n> = {
  court: { fr: 'Court terme', en: 'Short term' },
  moyen: { fr: 'Moyen terme', en: 'Mid term' },
  long: { fr: 'Long terme', en: 'Long term' },
};
export function isHorizon(v: unknown): v is Horizon {
  return typeof v === 'string' && (HORIZONS as string[]).includes(v);
}
